import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../services/api';
import { useCity } from '../contexts/CityContext';
import styles from './BusLineDetailPage.module.css';
import LineChip from '../components/common/LineChip';
import { MdTrain } from 'react-icons/md';
import { processTimetables } from '../utils/transportUtils';

// Tren hattı veri tipini tanımla
interface TrainLineDetail {
  id: string;
  bus_line_id: string;
  bus_line_name: string;
  bus_line_color?: string;
  bus_line_path_description: string;
  timetables?: any[];
  schedule?: any;
  // Diğer gerekli özellikler
}

function TrainLineDetailPage() {
  const { lineId } = useParams<{ lineId: string }>();
  const [lineDetail, setLineDetail] = useState<TrainLineDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'stops' | 'schedule' | 'map'>('details');
  const api = useApi();
  const { currentCity, loading: cityLoading } = useCity();
  const navigate = useNavigate();

  const transportMode = 'train';

  const getModeIcon = () => <MdTrain />;
  const getModeTitle = () => 'Tren';

  const fetchLineDetail = useCallback(async () => {
    if (cityLoading || !currentCity || !lineId) {
      setError('Lütfen önce bir şehir seçin ve geçerli bir hat numarası girin.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.getTransportLine(transportMode, lineId);

      if (response && response.status === 'success' && response.data) {
        const lineData = response.data as TrainLineDetail;

        if (lineData.timetables && lineData.timetables.length > 0) {
          lineData.schedule = processTimetables(lineData.timetables);
        }

        setLineDetail(lineData);
      } else {
        setError('Hat bilgisi bulunamadı.');
      }
    } catch (err) {
      setError('Hat bilgileri yüklenirken bir sorun oluştu.');
    } finally {
      setLoading(false);
    }
  }, [lineId, transportMode, currentCity, cityLoading, api]);

  useEffect(() => {
    fetchLineDetail();
  }, [fetchLineDetail]);

  return (
    <div className={styles.lineDetailPage}>
      <div className={styles.backButtonContainer}>
        <button className={styles.backButton} onClick={() => navigate('/lines')}>
          ← Hatlara Dön
        </button>
      </div>

      {loading && <div className={styles.loadingMessage}>Yükleniyor...</div>}

      {error && <div className={styles.errorMessage}>{error}</div>}

      {!loading && !error && lineDetail && (
        <>
          <div className={styles.lineHeader}>
            <div className={styles.modeIndicator}>
              {getModeIcon()}
              <span>{getModeTitle()}</span>
            </div>
            <LineChip lineNumber={lineDetail.bus_line_id} size="large" lineColor={lineDetail.bus_line_color || undefined} />
            <h1 className={styles.lineName}>{lineDetail.bus_line_name}</h1>
          </div>

          <div className={styles.tabMenu}>
            <button
              className={`${styles.tabButton} ${activeTab === 'details' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Detaylar
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'stops' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('stops')}
            >
              Duraklar
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'schedule' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('schedule')}
            >
              Tarife
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'map' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('map')}
            >
              Harita
            </button>
          </div>

          {activeTab === 'details' && (
            <div className={styles.lineDetailsCard}>
              <h3>Güzergah Açıklaması</h3>
              <p>{lineDetail.bus_line_path_description}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TrainLineDetailPage;